import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { PiggyBank, Tag as TagIcon } from 'lucide-react';
import { ShareSavingsButton } from '../ShareSavingsButton';

/**
 * Compact "Saved · Claims" pill + share button shown above the stats cards.
 * Sources its numbers from the parent's pre-fetched summary so we only hit
 * `/api/services/my-claims` once on dashboard load.
 */
export function SavingsStrip({ savingsSummary, user }) {
  const { api } = useAuth();
  const [qr, setQr] = useState(null);
  useEffect(() => { api.get('/api/member/savings').then(({ data }) => setQr(data)).catch(() => {}); }, [api]);
  // Total = discount claims + in-store QR redemptions (two separate sources).
  const totalSaved = Number(savingsSummary.total_savings || 0) + Number(qr?.total_saved || 0);
  const totalUses = Number(savingsSummary.total || 0) + Number(qr?.visits || 0);
  return (
    <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
      <Link
        to="/my-discounts"
        className="group inline-flex w-full sm:w-auto items-center gap-3 sm:gap-5 rounded-xl border border-[#00E676]/25 bg-gradient-to-r from-[#00E676]/10 via-[#00E5FF]/5 to-transparent px-4 py-3 hover:border-[#00E676]/50 transition-colors flex-1 sm:flex-initial"
        data-testid="dashboard-savings-strip"
      >
        <div className="flex items-center gap-2">
          <PiggyBank className="w-4 h-4 text-[#00E676]" />
          <span className="text-[11px] uppercase tracking-wider text-[#A0B4CC]">Saved</span>
          <span className="font-outfit text-white font-semibold tabular-nums">
            {`PHP ${totalSaved.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </span>
        </div>
        <span className="hidden sm:inline-block h-4 w-px bg-white/10" />
        <div className="flex items-center gap-2">
          <TagIcon className="w-4 h-4 text-[#00E5FF]" />
          <span className="text-[11px] uppercase tracking-wider text-[#A0B4CC]">Claims</span>
          <span className="font-outfit text-white font-semibold tabular-nums">
            {totalUses.toLocaleString()}
          </span>
        </div>
        <span className="ml-auto text-[11px] text-[#00E5FF] group-hover:text-white whitespace-nowrap">View →</span>
      </Link>
      <PaybackMeter saved={totalSaved} info={qr} />
      <ShareSavingsButton
        savings={totalSaved}
        claims={totalUses}
        referralCode={user?.referral_code}
        testid="dashboard-share-savings"
      />
    </div>
  );
}


/** Goal-gradient nudge: how much of the membership fee discounts have already paid back. */
function PaybackMeter({ saved, info }) {
  if (!info?.plan_price) return null;
  const total = saved;
  const pct = Math.min(100, Math.round((100 * total) / info.plan_price));
  const left = Math.max(0, info.plan_price - total);
  return (
    <Link to="/marketplace" className="flex-1 rounded-xl border border-white/10 bg-[#041A3B] px-4 py-3 hover:border-[#00E5FF]/40 transition-colors min-w-0" data-testid="dashboard-payback-meter">
      <div className="flex justify-between text-[11px] text-[#A0B4CC] mb-1.5 gap-2">
        <span className="uppercase tracking-wider">Membership paid back</span>
        <span className="text-white font-semibold" data-testid="dashboard-payback-pct">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#00E676] to-[#00E5FF] transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[11px] text-[#5C7599] mt-1.5 truncate">
        {left > 0 ? `PHP ${Math.round(left).toLocaleString()} more in discounts and it has paid for itself — find a deal →` : 'Your membership has paid for itself. Everything now is pure savings.'}
      </p>
    </Link>
  );
}
